import React, { useState, useRef } from 'react';
import { CUSTOMISE_CONTENT, SHAPES, WHATSAPP_NUMBER } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ChevronDown, Check, Sparkles, ShieldCheck, Diamond, MessageCircle, ArrowRight } from 'lucide-react';

const JOURNEY_NAV = [
  { id: 'step-1', num: '01', label: 'PIECE' },
  { id: 'step-2', num: '02', label: 'DIAMOND' },
  { id: 'step-3', num: '03', label: 'SETTING' },
  { id: 'step-4', num: '04', label: 'PERSONALISE' },
  { id: 'step-5', num: '05', label: 'FINISH' },
];

const WIZARD_STEPS = [
  { step: 1, num: '01', label: 'PIECE' },
  { step: 2, num: '02', label: 'DIAMOND' },
  { step: 3, num: '03', label: 'SETTING' },
  { step: 4, num: '04', label: 'PERSONALISE' },
  { step: 5, num: '05', label: 'FINISH' },
];

const Customise = () => {
  const { hero, intro, step1, step2, step3, step4, step5, process, faqs, possibilities } = CUSTOMISE_CONTENT;

  // Interactive Configuration State
  const [selectedPiece, setSelectedPiece] = useState('Rings');
  const [selectedShape, setSelectedShape] = useState('Oval');
  const [selectedSetting, setSelectedSetting] = useState('Solitaire');
  const [selectedPersonalisations, setSelectedPersonalisations] = useState([
    'Engraving (initials, dates, or a short message)'
  ]);
  const [selectedFinish, setSelectedFinish] = useState('Yellow Gold');

  // Multi-step Wizard Form State
  const [activeFormStep, setActiveFormStep] = useState(1);
  const [validationError, setValidationError] = useState('');

  // Accordion & Process Carousel States
  const [is4CsOpen, setIs4CsOpen] = useState(false);
  const [activeProcessIndex, setActiveProcessIndex] = useState(0);
  const processCarouselRef = useRef(null);

  const journeyRef = useRef(null);

  const scrollToStep = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToWizard = () => {
    const el = document.getElementById('build-your-custom-piece');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const togglePersonalisation = (option) => {
    setSelectedPersonalisations((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  const validateStep = (step) => {
    setValidationError('');
    if (step === 1 && !selectedPiece) {
      setValidationError('Please select a piece type to continue.');
      return false;
    }
    if (step === 2 && !selectedShape) {
      setValidationError('Please select a diamond shape to continue.');
      return false;
    }
    if (step === 3 && !selectedSetting) {
      setValidationError('Please select a setting to continue.');
      return false;
    }
    if (step === 5 && !selectedFinish) {
      setValidationError('Please select a metal finish to continue.');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeFormStep)) {
      setActiveFormStep((prev) => Math.min(prev + 1, 5));
      scrollToWizard();
    }
  };

  const handleBack = () => {
    setValidationError('');
    setActiveFormStep((prev) => Math.max(prev - 1, 1));
    scrollToWizard();
  };

  const handleProcessScroll = () => {
    if (!processCarouselRef.current) return;
    const scrollLeft = processCarouselRef.current.scrollLeft;
    const width = processCarouselRef.current.offsetWidth;
    if (width > 0) {
      const idx = Math.round(scrollLeft / (width * 0.8));
      setActiveProcessIndex(Math.min(Math.max(idx, 0), process.steps.length - 1));
    }
  };

  // Build dynamic WhatsApp prefilled message
  const buildCustomWhatsAppMessage = () => {
    const persText =
      selectedPersonalisations.length > 0
        ? selectedPersonalisations.join(', ')
        : 'None selected';
    const text = `Hi House of Seya! I'd like to start a custom piece:\n\n• Foundation Piece: ${selectedPiece}\n• Diamond Shape: ${selectedShape}\n• Setting: ${selectedSetting}\n• Personalisation: ${persText}\n• Finish: ${selectedFinish}`;
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
  };

  // Metal Swatches config
  const finishSwatches = {
    'Yellow Gold': {
      bg: '#E5C378',
      label: '24K Gold Vermeil',
      sub: 'Warm 2.5µ Gold over BIS Sterling Silver'
    },
    'Rose Gold': {
      bg: '#E0A996',
      label: '24K Rose Gold Vermeil',
      sub: 'Soft Blush 2.5µ Gold over BIS Sterling Silver'
    },
    'White Gold': {
      bg: '#E2E4E8',
      label: '24K White Gold Vermeil',
      sub: 'Crisp Platinum-tone 2.5µ Gold over BIS Sterling Silver'
    }
  };

  // Setting descriptions
  const settingDescriptions = {
    Solitaire: 'Pure, uncluttered focus on your central certified diamond.',
    Halo: 'Encircled by brilliant accent stones to enhance scale and radiance.',
    Pavé: 'A continuous line of delicate brilliance along the band.',
    Bezel: 'Sleek metal outline encasing the diamond for daily protection.',
    Cluster: 'An artistic composition of complementary lab-grown diamonds.'
  };

  return (
    <main className="bg-[#FFFFFF] text-[#1a1a1a] selection:bg-[var(--brand-green-tint-bg)] selection:text-[var(--brand-green)] font-sans">
      
      {/* 1. EDITORIAL HERO — "YOUR DIAMOND, YOUR DESIGN" */}
      <section className="relative h-[70vh] min-h-[520px] lg:h-[82vh] hero-vh-70-86 overflow-hidden">
        <img
          src={hero.image}
          alt="House of Seya Custom Fine Jewellery Atelier"
          className="w-full h-full object-cover object-center filter-none opacity-100"
          style={{ filter: 'none', opacity: 1 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/25 flex items-center justify-center">
          <div className="text-center text-white px-6 max-w-4xl mx-auto">
            <div className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-accent)] mb-4 font-medium">
              House of Seya Custom Atelier
            </div>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-8xl leading-[1.05] font-normal whitespace-pre-line text-[var(--brand-cream)] tracking-tight">
              {hero.headline}
            </h1>
            <p className="mt-6 text-sm sm:text-base lg:text-lg text-[#EDE9D6] max-w-2xl mx-auto leading-relaxed font-sans font-light">
              {hero.subhead}
            </p>
            <div className="mt-9">
              <button
                type="button"
                onClick={() => scrollToStep('customise-journey')}
                className="btn-cta focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#999959] cursor-pointer"
              >
                {hero.cta}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 2. EDITORIAL INTRO / BREATHING ROOM */}
      <section className="py-20 lg:py-28 bg-[#FFFFFF] border-b border-[#EEF1E4]">
        <div className="max-w-[1000px] mx-auto px-6 text-center">
          <div className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-semibold mb-3">
            YOUR CUSTOM JOURNEY
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#1a1a1a] leading-relaxed font-normal">
            At House of Seya, every custom piece begins with a conversation and a single, brilliant diamond.
          </h2>
          <div className="w-12 h-px bg-[var(--brand-sage)] mx-auto my-8 opacity-60" />
          <p className="text-sm sm:text-base text-[#5a5346] leading-relaxed max-w-2xl mx-auto font-sans">
            {intro ? intro.body : step1.body}
          </p>
        </div>
      </section>

      {/* 3. DESIGN JOURNEY VISUAL NAVIGATION BAR */}
      <nav
        id="customise-journey"
        ref={journeyRef}
        className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-[#EEF1E4] py-4 shadow-sm scroll-mt-0"
        aria-label="Design Journey Steps"
      >
        <div className="max-w-[1400px] mx-auto px-4 lg:px-12">
          <div className="flex items-center justify-between overflow-x-auto no-scrollbar gap-4 sm:gap-8 text-xs">
            {JOURNEY_NAV.map((navItem) => (
              <button
                key={navItem.id}
                type="button"
                onClick={() => scrollToStep(navItem.id)}
                className="flex items-center gap-2 flex-shrink-0 py-1 text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--brand-green)] group cursor-pointer"
              >
                <span className="font-mono text-[10px] text-[var(--brand-green-tint-accent)] font-semibold group-hover:text-[var(--brand-green)] transition-colors">
                  {navItem.num}
                </span>
                <span className="font-sans text-[11px] tracking-widest uppercase text-[#5a5346] group-hover:text-[var(--brand-green)] transition-colors">
                  {navItem.label}
                </span>
              </button>
            ))}
            <button
              type="button"
              onClick={scrollToWizard}
              className="flex items-center gap-2 flex-shrink-0 px-3.5 py-1.5 bg-[var(--brand-green)] text-white text-[10px] tracking-widest uppercase font-medium rounded-sm hover:bg-[var(--brand-green-tint-accent)] transition-colors cursor-pointer"
            >
              <span>Build Piece</span>
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </nav>

      {/* ================================================== */}
      {/* PART 1 — EXPLAIN THE CUSTOM JOURNEY (INFORMATIONAL ONLY) */}
      {/* ================================================== */}

      {/* STEP 1 INFORMATIONAL — CHOOSE YOUR PIECE */}
      <section id="step-1" className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF] scroll-mt-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Photography Card */}
            <div className="lg:col-span-5 relative group">
              <div className="aspect-[4/5] overflow-hidden bg-[#FFFFFF] border border-[#EEF1E4]">
                <img
                  src={possibilities[0].image}
                  alt={possibilities[0].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 filter-none opacity-100"
                  style={{ filter: 'none', opacity: 1 }}
                />
              </div>
              <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 text-xs font-serif text-[#1a1a1a] border border-[#EEF1E4] shadow-sm">
                {possibilities[0].title}
              </div>
            </div>

            {/* Right Column: Editorial Instructions */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-mono uppercase tracking-widest px-2.5 py-0.5 bg-[var(--brand-green-tint-bg)] text-[var(--brand-green-tint-accent)] font-semibold">
                  01
                </span>
                <span className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-medium">
                  Foundation Piece
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
                {step1.title}
              </h2>

              <p className="mt-5 text-sm sm:text-base text-[#5a5346] leading-relaxed max-w-xl">
                {step1.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STEP 2 INFORMATIONAL — CHOOSE YOUR DIAMOND */}
      <section id="step-2" className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF] scroll-mt-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start mb-16">
            {/* Left Column: Diamond Narrative & Micro Education */}
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-mono uppercase tracking-widest px-2.5 py-0.5 bg-[var(--brand-green-tint-bg)] text-[var(--brand-green-tint-accent)] font-semibold">
                  02
                </span>
                <span className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-medium">
                  The Centerpiece
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
                {step2.title}
              </h2>

              <div className="mt-5 space-y-4 text-sm sm:text-base text-[#5a5346] leading-relaxed">
                <p>{step2.body1}</p>
                <p>{step2.body2}</p>
              </div>

              {/* Refined "Why Cut Matters Most" Callout Box */}
              <div className="mt-8 p-6 bg-[var(--brand-green-tint-bg)] border-l-2 border-[var(--brand-green-tint-accent)]">
                <div className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-semibold mb-2 flex items-center gap-2">
                  <Sparkles size={14} />
                  <span>Why Cut Matters Most</span>
                </div>
                <p className="text-xs sm:text-sm text-[#2B4019] leading-relaxed font-sans">
                  {step2.microCallout.replace('Why cut matters most: ', '')}
                </p>
              </div>
            </div>

            {/* Right Column: Diamond Photography Card */}
            <div className="lg:col-span-6">
              <div className="aspect-[4/3] overflow-hidden bg-white border border-[#EEF1E4] shadow-sm relative group">
                <img
                  src={possibilities[1].image}
                  alt={possibilities[1].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 filter-none opacity-100"
                  style={{ filter: 'none', opacity: 1 }}
                />
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 text-xs font-serif text-[#1a1a1a] border border-[#EEF1E4] shadow-sm">
                  {possibilities[1].title}
                </div>
              </div>
            </div>
          </div>

          {/* 4Cs Education Expandable Section */}
          <div className="mt-10 border border-[#E5E7EB] bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => setIs4CsOpen(!is4CsOpen)}
              className="w-full p-6 flex items-center justify-between text-left hover:bg-[#FFFFFF] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)] cursor-pointer"
              aria-expanded={is4CsOpen}
              aria-controls="4cs-guide-panel"
            >
              <div className="flex items-center gap-3">
                <ShieldCheck size={20} className="text-[var(--brand-green-tint-accent)]" />
                <div>
                  <span className="font-serif text-lg text-[#1a1a1a]">
                    The 4Cs Education Guide
                  </span>
                  <span className="block text-xs text-[#5a5346] mt-0.5">
                    Cut · Colour · Clarity · Carat Weight Essentials
                  </span>
                </div>
              </div>
              <ChevronDown
                size={18}
                className={`text-[#1a1a1a] transition-transform duration-300 ${
                  is4CsOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {is4CsOpen && (
              <div
                id="4cs-guide-panel"
                className="p-6 sm:p-8 border-t border-[#EEF1E4] bg-[#FFFFFF] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                <div className="p-5 bg-white border border-[#EEF1E4]">
                  <div className="text-xs tracking-widest-2 uppercase text-[var(--brand-sage)] font-semibold mb-1">
                    Cut
                  </div>
                  <p className="text-xs text-[#5a5346] leading-relaxed font-sans">
                    Determines light reflection and brilliance. Cut quality is prioritized above all else.
                  </p>
                </div>
                <div className="p-5 bg-white border border-[#EEF1E4]">
                  <div className="text-xs tracking-widest-2 uppercase text-[var(--brand-sage)] font-semibold mb-1">
                    Colour
                  </div>
                  <p className="text-xs text-[#5a5346] leading-relaxed font-sans">
                    Graded D to Z. House of Seya selects stones strictly in the D–F range (colourless).
                  </p>
                </div>
                <div className="p-5 bg-white border border-[#EEF1E4]">
                  <div className="text-xs tracking-widest-2 uppercase text-[var(--brand-sage)] font-semibold mb-1">
                    Clarity
                  </div>
                  <p className="text-xs text-[#5a5346] leading-relaxed font-sans">
                    Measures natural inclusions. Our standard is VVS1–VS2 for completely eye-clean stones.
                  </p>
                </div>
                <div className="p-5 bg-white border border-[#EEF1E4]">
                  <div className="text-xs tracking-widest-2 uppercase text-[var(--brand-sage)] font-semibold mb-1">
                    Carat
                  </div>
                  <p className="text-xs text-[#5a5346] leading-relaxed font-sans">
                    Precise weight measurement. Every stone arrives with an independent IGI certificate.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* STEP 3 INFORMATIONAL — CHOOSE YOUR SETTING */}
      <section id="step-3" className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF] scroll-mt-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Setting Content */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-mono uppercase tracking-widest px-2.5 py-0.5 bg-[var(--brand-green-tint-bg)] text-[var(--brand-green-tint-accent)] font-semibold">
                  03
                </span>
                <span className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-medium">
                  The Architecture
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
                {step3.title}
              </h2>

              <div className="mt-5 space-y-4 text-sm sm:text-base text-[#5a5346] leading-relaxed max-w-xl">
                <p>{step3.body1}</p>
                <p>{step3.body2}</p>
              </div>
            </div>

            {/* Right Column: Setting Photography Card */}
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden bg-[#FFFFFF] border border-[#EEF1E4] relative group">
                <img
                  src={possibilities[2].image}
                  alt={possibilities[2].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 filter-none opacity-100"
                  style={{ filter: 'none', opacity: 1 }}
                />
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 text-xs font-serif text-[#1a1a1a] border border-[#EEF1E4] shadow-sm">
                  {possibilities[2].title}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STEP 4 INFORMATIONAL — PERSONALISE IT */}
      <section id="step-4" className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF] scroll-mt-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Column: Photography Card */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="aspect-[4/5] overflow-hidden bg-white border border-[#EEF1E4] relative group">
                <img
                  src={possibilities[3].image}
                  alt={possibilities[3].title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 filter-none opacity-100"
                  style={{ filter: 'none', opacity: 1 }}
                />
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 text-xs font-serif text-[#1a1a1a] border border-[#EEF1E4] shadow-sm">
                  {possibilities[3].title}
                </div>
              </div>
            </div>

            {/* Right Column: Narrative */}
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs font-mono uppercase tracking-widest px-2.5 py-0.5 bg-[var(--brand-green-tint-bg)] text-[var(--brand-green-tint-accent)] font-semibold">
                  04
                </span>
                <span className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-medium">
                  Personal Touches
                </span>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
                {step4.title}
              </h2>

              <p className="mt-5 text-sm sm:text-base text-[#5a5346] leading-relaxed max-w-xl">
                {step4.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STEP 5 INFORMATIONAL — CHOOSE YOUR FINISH */}
      <section id="step-5" className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF] scroll-mt-14">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-mono uppercase tracking-widest px-2.5 py-0.5 bg-[var(--brand-green-tint-bg)] text-[var(--brand-green-tint-accent)] font-semibold">
                05
              </span>
              <span className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-medium">
                The Metal Standard
              </span>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal leading-tight">
              {step5.title}
            </h2>

            <p className="mt-5 text-sm sm:text-base text-[#5a5346] leading-relaxed">
              {step5.body}
            </p>
          </div>
        </div>
      </section>

      {/* ================================================== */}
      {/* PART 2 — ACTUAL CUSTOMIZATION INTERACTIVE WIZARD */}
      {/* ================================================== */}
      <section
        id="build-your-custom-piece"
        className="py-20 lg:py-28 bg-[var(--brand-green-tint-bg)]/40 border-b border-[#EEF1E4] scroll-mt-14"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-semibold block mb-2">
              INTERACTIVE BUILDER
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] font-normal">
              Build Your Custom Piece
            </h2>
            <p className="mt-3 text-sm sm:text-base text-[#5a5346] font-sans">
              Select your preferences step-by-step below to configure your bespoke jewellery design.
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="max-w-4xl mx-auto mb-12 px-2">
            <div className="flex items-center justify-between border-b border-[#EEF1E4] pb-4 overflow-x-auto no-scrollbar gap-2">
              {WIZARD_STEPS.map((s) => {
                const isCurrent = activeFormStep === s.step;
                const isCompleted = activeFormStep > s.step;
                return (
                  <button
                    key={s.step}
                    type="button"
                    onClick={() => {
                      if (s.step <= activeFormStep || validateStep(activeFormStep)) {
                        setActiveFormStep(s.step);
                        scrollToWizard();
                      }
                    }}
                    className={`flex items-center gap-2 flex-shrink-0 transition-colors cursor-pointer py-1 ${
                      isCurrent
                        ? 'text-[var(--brand-green)] font-semibold'
                        : isCompleted
                        ? 'text-[#1a1a1a] font-medium'
                        : 'text-[#999959] opacity-60'
                    }`}
                  >
                    <span
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono border ${
                        isCurrent
                          ? 'bg-[var(--brand-green)] text-white border-[var(--brand-green)]'
                          : isCompleted
                          ? 'bg-[var(--brand-green-tint-bg)] text-[var(--brand-green)] border-[var(--brand-green)]'
                          : 'bg-white text-[#999959] border-[#E5E7EB]'
                      }`}
                    >
                      {s.num}
                    </span>
                    <span className="text-[11px] tracking-wider uppercase font-sans hidden sm:inline">
                      {s.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Validation Error Message */}
          {validationError && (
            <div className="max-w-xl mx-auto mb-6 p-4 bg-rose-50 border border-rose-200 text-rose-800 text-xs sm:text-sm rounded-sm text-center font-sans">
              {validationError}
            </div>
          )}

          {/* FORM STEP 1 — CHOOSE YOUR PIECE */}
          {activeFormStep === 1 && (
            <div className="max-w-3xl mx-auto bg-white p-6 sm:p-10 border border-[#E5E7EB] shadow-sm">
              <div className="mb-6 text-center">
                <span className="text-xs font-mono text-[var(--brand-green-tint-accent)] uppercase tracking-widest font-semibold block mb-1">
                  STEP 01 OF 05
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#1a1a1a]">
                  Choose Your Piece
                </h3>
                <p className="text-xs sm:text-sm text-[#5a5346] mt-2 max-w-lg mx-auto">
                  Select the foundation item for your custom piece.
                </p>
              </div>

              <fieldset className="mt-8">
                <legend className="sr-only">Choose your piece</legend>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {step1.options.map((option) => {
                    const isSelected = selectedPiece === option;
                    const id = `wiz-piece-${option.toLowerCase()}`;
                    return (
                      <div key={option} className="relative">
                        <input
                          type="radio"
                          id={id}
                          name="wiz_piece"
                          value={option}
                          checked={isSelected}
                          onChange={() => {
                            setSelectedPiece(option);
                            setValidationError('');
                          }}
                          className="sr-only peer"
                        />
                        <label
                          htmlFor={id}
                          className={`p-5 text-center transition-all duration-300 border-2 rounded-sm flex flex-col items-center justify-center gap-3 cursor-pointer focus-within:ring-2 focus-within:ring-[var(--brand-green)] ${
                            isSelected
                              ? 'bg-[var(--brand-green-tint-bg)] border-[var(--brand-green)] text-[var(--brand-green)] shadow-sm'
                              : 'bg-white text-[#1a1a1a] border-[#E5E7EB] hover:border-[var(--brand-sage)]'
                          }`}
                        >
                          <span className={`font-serif text-lg sm:text-xl ${isSelected ? 'font-medium text-[var(--brand-green)]' : 'font-normal text-[#1a1a1a]'}`}>
                            {option}
                          </span>
                          <div
                            className={`w-5 h-5 rounded-full flex items-center justify-center border transition-colors ${
                              isSelected
                                ? 'bg-[var(--brand-green)] border-[var(--brand-green)] text-white'
                                : 'border-[#CCCCCC] bg-white'
                            }`}
                          >
                            {isSelected && <Check size={13} strokeWidth={3} />}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>

              <div className="mt-10 flex justify-end">
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-8 py-3.5 bg-[var(--brand-green)] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[var(--brand-green-accent)] hover:text-[var(--brand-green)] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)] cursor-pointer text-center min-w-[120px]"
                >
                  NEXT
                </button>
              </div>
            </div>
          )}

          {/* FORM STEP 2 — CHOOSE YOUR DIAMOND */}
          {activeFormStep === 2 && (
            <div className="max-w-4xl mx-auto bg-white p-6 sm:p-10 border border-[#E5E7EB] shadow-sm">
              <div className="mb-6 text-center">
                <span className="text-xs font-mono text-[var(--brand-green-tint-accent)] uppercase tracking-widest font-semibold block mb-1">
                  STEP 02 OF 05
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#1a1a1a]">
                  Choose Your Diamond
                </h3>
                <p className="text-xs sm:text-sm text-[#5a5346] mt-2 max-w-lg mx-auto">
                  Select your preferred certified diamond silhouette.
                </p>
              </div>

              <fieldset className="mt-8">
                <legend className="sr-only">Choose your diamond shape</legend>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3.5">
                  {step2.shapes.map((shapeName) => {
                    const isSelected = selectedShape === shapeName;
                    const shapeObj = SHAPES.find((s) => s.name === shapeName);
                    const id = `wiz-shape-${shapeName.toLowerCase()}`;
                    return (
                      <div key={shapeName} className="relative">
                        <input
                          type="radio"
                          id={id}
                          name="wiz_shape"
                          value={shapeName}
                          checked={isSelected}
                          onChange={() => {
                            setSelectedShape(shapeName);
                            setValidationError('');
                          }}
                          className="sr-only peer"
                        />
                        <label
                          htmlFor={id}
                          className={`p-3.5 flex flex-col items-center justify-center transition-all duration-300 border-2 rounded-sm cursor-pointer h-full focus-within:ring-2 focus-within:ring-[var(--brand-green)] ${
                            isSelected
                              ? 'bg-[var(--brand-green-tint-bg)] border-[var(--brand-green)] text-[var(--brand-green)] shadow-sm'
                              : 'bg-[#FFFFFF] border-[#E5E7EB] text-[#1a1a1a] hover:border-[var(--brand-sage)] hover:bg-white'
                          }`}
                        >
                          {shapeObj ? (
                            <div className="w-12 h-12 flex items-center justify-center mb-2 bg-white rounded-full p-1.5 border border-[#EEF1E4]">
                              <img
                                src={shapeObj.img}
                                alt=""
                                aria-hidden="true"
                                className="w-9 h-9 object-contain opacity-100 filter-none"
                                style={{ filter: 'none', opacity: 1 }}
                              />
                            </div>
                          ) : (
                            <Diamond size={22} className="mb-2 opacity-80" />
                          )}
                          <div className="flex items-center gap-1">
                            <span className={`text-xs font-serif tracking-wider ${isSelected ? 'font-semibold text-[var(--brand-green)]' : 'font-normal text-[#1a1a1a]'}`}>
                              {shapeName}
                            </span>
                            {isSelected && <Check size={12} className="text-[var(--brand-green)]" />}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>

              <div className="mt-10 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-7 py-3.5 border border-[#1a1a1a] text-xs font-semibold tracking-widest uppercase text-[#1a1a1a] bg-transparent hover:bg-[#1a1a1a] hover:text-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a1a1a] cursor-pointer text-center min-w-[100px] sm:min-w-[120px]"
                >
                  BACK
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-8 py-3.5 bg-[var(--brand-green)] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[var(--brand-green-accent)] hover:text-[var(--brand-green)] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)] cursor-pointer text-center min-w-[100px] sm:min-w-[120px]"
                >
                  NEXT
                </button>
              </div>
            </div>
          )}

          {/* FORM STEP 3 — CHOOSE YOUR SETTING */}
          {activeFormStep === 3 && (
            <div className="max-w-4xl mx-auto bg-white p-6 sm:p-10 border border-[#E5E7EB] shadow-sm">
              <div className="mb-6 text-center">
                <span className="text-xs font-mono text-[var(--brand-green-tint-accent)] uppercase tracking-widest font-semibold block mb-1">
                  STEP 03 OF 05
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#1a1a1a]">
                  Choose Your Setting
                </h3>
                <p className="text-xs sm:text-sm text-[#5a5346] mt-2 max-w-lg mx-auto">
                  Select the setting style for your certified diamond.
                </p>
              </div>

              <fieldset className="mt-8">
                <legend className="sr-only">Choose your setting architecture</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {step3.settings.map((setting) => {
                    const isSelected = selectedSetting === setting;
                    const id = `wiz-setting-${setting.toLowerCase()}`;
                    return (
                      <div key={setting} className="relative">
                        <input
                          type="radio"
                          id={id}
                          name="wiz_setting"
                          value={setting}
                          checked={isSelected}
                          onChange={() => {
                            setSelectedSetting(setting);
                            setValidationError('');
                          }}
                          className="sr-only peer"
                        />
                        <label
                          htmlFor={id}
                          className={`p-5 text-left transition-all duration-300 border-2 rounded-sm flex flex-col justify-between cursor-pointer h-full focus-within:ring-2 focus-within:ring-[var(--brand-green)] ${
                            isSelected
                              ? 'bg-[var(--brand-green-tint-bg)] text-[var(--brand-green)] border-[var(--brand-green)] shadow-sm'
                              : 'bg-white text-[#1a1a1a] border-[#E5E7EB] hover:border-[var(--brand-sage)]'
                          }`}
                        >
                          <div>
                            <div className="font-serif text-xl mb-2 font-normal flex items-center justify-between">
                              <span>{setting}</span>
                              <div
                                className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border transition-colors ${
                                  isSelected
                                    ? 'bg-[var(--brand-green)] border-[var(--brand-green)] text-white'
                                    : 'border-[#CCCCCC] bg-white'
                                }`}
                              >
                                {isSelected && <Check size={13} strokeWidth={3} />}
                              </div>
                            </div>
                            <p className="text-xs leading-relaxed text-[#5a5346] font-sans">
                              {settingDescriptions[setting]}
                            </p>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>

              <div className="mt-10 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-7 py-3.5 border border-[#1a1a1a] text-xs font-semibold tracking-widest uppercase text-[#1a1a1a] bg-transparent hover:bg-[#1a1a1a] hover:text-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a1a1a] cursor-pointer text-center min-w-[100px] sm:min-w-[120px]"
                >
                  BACK
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-8 py-3.5 bg-[var(--brand-green)] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[var(--brand-green-accent)] hover:text-[var(--brand-green)] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)] cursor-pointer text-center min-w-[100px] sm:min-w-[120px]"
                >
                  NEXT
                </button>
              </div>
            </div>
          )}

          {/* FORM STEP 4 — PERSONALISE IT */}
          {activeFormStep === 4 && (
            <div className="max-w-3xl mx-auto bg-white p-6 sm:p-10 border border-[#E5E7EB] shadow-sm">
              <div className="mb-6 text-center">
                <span className="text-xs font-mono text-[var(--brand-green-tint-accent)] uppercase tracking-widest font-semibold block mb-1">
                  STEP 04 OF 05
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#1a1a1a]">
                  Personalise It
                </h3>
                <p className="text-xs sm:text-sm text-[#5a5346] mt-2 max-w-lg mx-auto">
                  Select one or more personal touches to add to your piece (Multiple Allowed).
                </p>
              </div>

              <fieldset className="mt-8">
                <legend className="sr-only">Select personalisation options</legend>
                <div className="space-y-4">
                  {step4.options.map((option, idx) => {
                    const isSelected = selectedPersonalisations.includes(option);
                    const id = `wiz-pers-${idx}`;
                    return (
                      <div key={option} className="relative">
                        <input
                          type="checkbox"
                          id={id}
                          name="wiz_personalisation"
                          value={option}
                          checked={isSelected}
                          onChange={() => togglePersonalisation(option)}
                          className="sr-only peer"
                        />
                        <label
                          htmlFor={id}
                          className={`w-full p-4 sm:p-5 text-left transition-all duration-300 border-2 rounded-sm flex items-center justify-between gap-4 cursor-pointer focus-within:ring-2 focus-within:ring-[var(--brand-green)] ${
                            isSelected
                              ? 'bg-[var(--brand-green-tint-bg)] text-[var(--brand-green)] border-[var(--brand-green)] shadow-sm'
                              : 'bg-white text-[#1a1a1a] border-[#E5E7EB] hover:border-[var(--brand-sage)]'
                          }`}
                        >
                          <span className="text-sm sm:text-base font-sans font-medium">
                            {option}
                          </span>
                          <div
                            className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border transition-colors ${
                              isSelected
                                ? 'bg-[var(--brand-green)] border-[var(--brand-green)] text-white'
                               : 'border-[#CCCCCC] bg-white'
                            }`}
                          >
                            {isSelected && <Check size={13} strokeWidth={3} />}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>

              <div className="mt-10 flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-7 py-3.5 border border-[#1a1a1a] text-xs font-semibold tracking-widest uppercase text-[#1a1a1a] bg-transparent hover:bg-[#1a1a1a] hover:text-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a1a1a] cursor-pointer text-center min-w-[100px] sm:min-w-[120px]"
                >
                  BACK
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-8 py-3.5 bg-[var(--brand-green)] text-white text-xs font-semibold tracking-widest uppercase hover:bg-[var(--brand-green-accent)] hover:text-[var(--brand-green)] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)] cursor-pointer text-center min-w-[100px] sm:min-w-[120px]"
                >
                  NEXT
                </button>
              </div>
            </div>
          )}

          {/* FORM STEP 5 — CHOOSE YOUR FINISH */}
          {activeFormStep === 5 && (
            <div className="max-w-4xl mx-auto bg-white p-6 sm:p-10 border border-[#E5E7EB] shadow-sm">
              <div className="mb-6 text-center">
                <span className="text-xs font-mono text-[var(--brand-green-tint-accent)] uppercase tracking-widest font-semibold block mb-1">
                  STEP 05 OF 05
                </span>
                <h3 className="font-serif text-2xl sm:text-3xl text-[#1a1a1a]">
                  Choose Your Finish
                </h3>
                <p className="text-xs sm:text-sm text-[#5a5346] mt-2 max-w-lg mx-auto">
                  Select your 24K gold vermeil metal finish standard.
                </p>
              </div>

              <fieldset className="mt-8">
                <legend className="sr-only">Choose your metal finish</legend>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {step5.finishes.map((finish) => {
                    const isSelected = selectedFinish === finish;
                    const swatch = finishSwatches[finish];
                    const id = `wiz-finish-${finish.toLowerCase().replace(/\s+/g, '-')}`;
                    return (
                      <div key={finish} className="relative">
                        <input
                          type="radio"
                          id={id}
                          name="wiz_finish"
                          value={finish}
                          checked={isSelected}
                          onChange={() => {
                            setSelectedFinish(finish);
                            setValidationError('');
                          }}
                          className="sr-only peer"
                        />
                        <label
                          htmlFor={id}
                          className={`p-5 text-left transition-all duration-300 border-2 rounded-sm flex flex-col justify-between cursor-pointer h-full focus-within:ring-2 focus-within:ring-[var(--brand-green)] ${
                            isSelected
                              ? 'bg-[var(--brand-green-tint-bg)] text-[var(--brand-green)] border-[var(--brand-green)] shadow-sm'
                              : 'bg-white text-[#1a1a1a] border-[#E5E7EB] hover:border-[var(--brand-sage)]'
                          }`}
                        >
                          <div>
                            <div className="flex items-center gap-3.5 mb-3">
                              <span
                                className="w-9 h-9 rounded-full border border-black/10 shadow-inner flex-shrink-0"
                                style={{ backgroundColor: swatch.bg }}
                                aria-hidden="true"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-serif text-xl font-normal flex items-center justify-between">
                                  <span>{finish}</span>
                                  <div
                                    className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center border transition-colors ${
                                      isSelected
                                        ? 'bg-[var(--brand-green)] border-[var(--brand-green)] text-white'
                                        : 'border-[#CCCCCC] bg-white'
                                    }`}
                                  >
                                    {isSelected && <Check size={13} strokeWidth={3} />}
                                  </div>
                                </div>
                                <div className="text-[11px] text-[#999959] font-medium">
                                  {swatch.label}
                                </div>
                              </div>
                            </div>

                            <p className="text-xs leading-relaxed border-t border-[#EEF1E4] pt-2.5 text-[#5a5346] font-sans">
                              {swatch.sub}
                            </p>
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>

              {/* Bespoke Summary inside Step 5 */}
              <div className="mt-10 p-6 bg-[var(--brand-green-tint-bg)] border border-[var(--brand-green-border)] text-center">
                <div className="text-xs uppercase tracking-widest text-[var(--brand-green-tint-accent)] font-semibold mb-2">
                  YOUR BESPOKE SELECTION SUMMARY
                </div>
                <div className="flex flex-wrap justify-center gap-3 text-xs sm:text-sm text-[#1a1a1a] mt-3">
                  <span className="px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-sm">Piece: <strong>{selectedPiece}</strong></span>
                  <span className="px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-sm">Shape: <strong>{selectedShape}</strong></span>
                  <span className="px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-sm">Setting: <strong>{selectedSetting}</strong></span>
                  <span className="px-3 py-1.5 bg-white border border-[#E5E7EB] rounded-sm">Finish: <strong>{selectedFinish}</strong></span>
                </div>
              </div>

              <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  className="w-full sm:w-auto px-7 py-3.5 border border-[#1a1a1a] text-xs font-semibold tracking-widest uppercase text-[#1a1a1a] bg-transparent hover:bg-[#1a1a1a] hover:text-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1a1a1a] cursor-pointer text-center"
                >
                  BACK
                </button>
                <a
                  href={buildCustomWhatsAppMessage()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-8 py-3.5 bg-[var(--brand-green-accent)] text-[var(--brand-green)] font-semibold text-xs tracking-widest uppercase hover:bg-[var(--brand-green)] hover:text-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)] text-center"
                >
                  <MessageCircle size={17} />
                  <span>START CUSTOMISING VIA WHATSAPP</span>
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 9. REVIEW & FINAL CONSULTATION CTA */}
      <section className="py-20 bg-[var(--brand-green)] text-white border-b border-[var(--brand-green-border)]">
        <div className="max-w-[1000px] mx-auto px-6 text-center">
          <div className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-accent)] font-medium mb-2">
            DESIGN REVIEW
          </div>
          <h3 className="font-serif text-3xl sm:text-5xl text-[var(--brand-cream)] leading-tight font-normal">
            Your Bespoke Selection
          </h3>

          {/* Interactive Live Summary Badges */}
          <div className="mt-8 flex flex-wrap justify-center gap-3 text-xs sm:text-sm text-[var(--brand-green-muted)]">
            <span className="px-4 py-2 bg-white/10 border border-white/15 rounded-sm">
              Piece: <strong className="text-white">{selectedPiece}</strong>
            </span>
            <span className="px-4 py-2 bg-white/10 border border-white/15 rounded-sm">
              Shape: <strong className="text-white">{selectedShape}</strong>
            </span>
            <span className="px-4 py-2 bg-white/10 border border-white/15 rounded-sm">
              Setting: <strong className="text-white">{selectedSetting}</strong>
            </span>
            <span className="px-4 py-2 bg-white/10 border border-white/15 rounded-sm">
              Finish: <strong className="text-white">{selectedFinish}</strong>
            </span>
          </div>

          <div className="mt-10">
            <a
              href={buildCustomWhatsAppMessage()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-9 py-4 bg-[var(--brand-green-accent)] text-[var(--brand-green)] font-semibold text-xs tracking-widest uppercase hover:bg-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <MessageCircle size={18} />
              <span>Start Customising via WhatsApp</span>
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* 10. FROM YOUR VISION TO YOUR DOORSTEP */}
      <section className="py-20 lg:py-28 border-b border-[#EEF1E4] bg-[#FFFFFF]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-[11px] tracking-widest-2 uppercase text-[var(--brand-green-tint-accent)] font-semibold">
              THE EXPERIENCE
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1a1a1a] mt-2 font-normal">
              {process.title}
            </h2>
            <p className="mt-4 text-sm sm:text-base text-[#5a5346] leading-relaxed font-sans">
              {process.body}
            </p>
          </div>

          <div className="mb-4">
            <div
              ref={processCarouselRef}
              onScroll={handleProcessScroll}
              className="flex sm:grid sm:grid-cols-3 gap-4 lg:gap-6 overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:snap-none scrollbar-hide -mx-6 px-6 sm:mx-0 sm:px-0 items-stretch"
            >
              {process.steps.map((step, index) => (
                <div
                  key={step.title}
                  className="flex-shrink-0 w-full sm:w-auto snap-center bg-white border border-[#E5E7EB] p-6 sm:p-8 shadow-sm rounded-sm text-center flex flex-col justify-between h-full min-h-[220px] sm:min-h-[180px] hover:border-[var(--brand-sage)] transition-colors duration-300"
                >
                  <div>
                    <div className="w-12 h-12 rounded-full border border-[var(--brand-green)] text-[var(--brand-green)] font-serif text-xl flex items-center justify-center mx-auto mb-5 font-normal">
                      {index + 1}
                    </div>
                    <h3 className="font-serif text-xl lg:text-2xl text-[#1a1a1a] mb-3 font-normal">
                      {step.title}
                    </h3>
                    <p className="text-sm text-[#5a5346] leading-relaxed font-sans">
                      {step.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 p-5 bg-[#FFFFFF] border border-[#E5E7EB] text-center max-w-3xl mx-auto">
            <p className="text-xs text-[#5a5346] italic leading-relaxed font-sans">
              {process.editorialNote}
            </p>
          </div>
        </div>
      </section>

      {/* 11. FREQUENTLY ASKED QUESTIONS */}
      <section className="py-20 lg:py-28 bg-[#FFFFFF]">
        <div className="max-w-[800px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="font-serif text-2xl lg:text-3xl text-[#1a1a1a]">
              Frequently Asked Questions
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className="border-b border-[#d8ceb9]"
              >
                <AccordionTrigger className="font-serif text-base lg:text-lg text-[#1a1a1a] py-5 hover:no-underline text-left">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-[14px] text-[#5a5346] leading-relaxed pb-5 font-sans">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

    </main>
  );
};

export default Customise;
